import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  ArrowRight,
  Briefcase,
  Check,
  ChevronRight,
  ClipboardCheck,
  ClipboardList,
  Eye,
  LifeBuoy,
  Loader2,
  RefreshCw,
  Sparkles,
  UserCheck,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { parseCalendarDate } from "./AccountPlansListTab";
import ChatPanel, { ChatSeed } from "./ChatPanel";
import { FLOW } from "./flowCopy";
import { planLanding } from "./landing";
import NoPlanYet from "./NoPlanYet";
import { aeName } from "./PlanCard";
import {
  awaitsBriefing,
  isChatReady,
  planFlow,
  withChatFile,
} from "./planFlow";
import PlanOverviewTab from "./PlanOverviewTab";
import PlanViewerTab from "./PlanViewerTab";
import PublishTab from "./PublishTab";
import QaTab from "./QaTab";
import {
  AskSeed,
  NextStepAction,
  NextStepSlot,
  PERSONA_LABEL,
  PERSONA_SHORT,
  PlanNav,
  PlanTab,
  STEPS,
  TONE_CLASS,
} from "./shared";
import SponsorViewTab from "./SponsorViewTab";
import PageHeader, { PageHeaderBreadcrumb } from "../../Components/PageHeader";
import { useAccountPlanOverview } from "../../Hooks/useAccountPlan";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  PLAN_NOT_AVAILABLE,
  fetchAccountPlansEnabled,
} from "../../Store/Reducers/AccountPlanSlice";
import type { AccountPlanOverview, Persona } from "../../Types/AccountPlan";
import {
  ROLES,
  isAccountTeamRole,
  normalizeRoleName,
} from "../../Utils/Constants/RoleConstants";
import {
  STORAGE_KEYS,
  getFromLocalStorage,
} from "../../Utils/Helper/LocalStorage";

const TABS: PlanTab[] = ["plan", "viewer", "sponsor", "qa", "publish"];
const PERSONAS: Persona[] = ["ES", "AE", "CSM"];

interface Props {
  accountId: string;
  /** Prefixes org-scoped links (/:orgSlug/...). */
  withOrg: (path: string) => string;
}

const formatMoney = (n?: number | null) =>
  n == null
    ? null
    : new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: "USD",
        notation: "compact",
        maximumFractionDigits: 1,
      }).format(n);

/**
 * Where a persona lands when no tab is in the URL: the shared NAV-01 rule
 * (`planLanding`, also used by the My Accounts row click). Only the tab is
 * taken here; an AE/CSM landing on Review briefing until the plan is published.
 */
const defaultTab = (persona: Persona, data: AccountPlanOverview): PlanTab => {
  const flow = planFlow(data);
  return planLanding(persona, {
    // Only discarded or stopped uploads: nothing to review (F-UF-E01).
    hasPlan: !!data.current_file_id || !!flow.toConfirm || !!flow.reading,
    published: data.current_review?.status === "published",
    // Waiting on the AE (read and unconfirmed, flagged, or an account warning they
    // may overrule): everyone on the team opens on Confirm plan, where the tick
    // or the reason is (F-UF-E01), as the list's Take action does.
    toConfirm: !!flow.toConfirm,
    reading: !data.current_file_id && !!flow.reading,
  }).tab;
};

/** Explains the Review briefing step's "N of M verified" counter. */
const REVIEW_HINT_HELP =
  "Sections confirmed by their owner. The AE's sections must be verified before you can publish; the CSM's are recommended.";

const PUBLISHED_HINT_HELP =
  "This version is published, so its review is done. Sections a CSM hadn't confirmed show as pending to the sponsor.";

const STILL_PUBLISHED_HELP =
  "Sponsors still read this published version until you publish the newer one.";

/** The element that scrolls the page: the layout's overflow pane, else the window. */
const scrollParent = (el: HTMLElement | null): HTMLElement | null => {
  for (let p = el?.parentElement; p; p = p.parentElement) {
    if (/(auto|scroll)/.test(getComputedStyle(p).overflowY)) return p;
  }
  return null;
};

const READER_NOTE =
  "You're reading the published briefing. You're not on this account's team, so the plan itself and its drafts stay with them.";
const READER_LOCKED =
  "This plan isn't available to you. Only the account's team can open it before it's published.";

/** A reader has the briefing and its questions, never the slides (View source). */
const readerTab = (reader: boolean, tab: PlanTab): PlanTab =>
  reader && tab === "viewer" ? "sponsor" : tab;

/** Tabs a persona can't use fold onto its nearest equivalent. */
const normalizeTab = (persona: Persona, tab: PlanTab): PlanTab =>
  persona === "ES" && (tab === "plan" || tab === "publish") ? "sponsor" : tab;

const AccountPlanContainer: React.FC<Props> = ({ accountId, withOrg }) => {
  const { data, loading, error, refresh, pollingStopped } =
    useAccountPlanOverview(accountId);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const fileId = params.get("file");
  const slide = Number(params.get("slide")) || null;
  const [chatOpen, setChatOpen] = useState(false);
  // The current step's next action, shown at the top too (product review 2026-11-01).
  const [topNext, setTopNext] = useState<NextStepAction | null>(null);
  // Plan chat is out of v1 scope: shown only where its own switch is on.
  const chatEnabled = useAppSelector(state => state.accountPlan.chatEnabled);
  const dispatch = useAppDispatch();
  const userRole = useAppSelector(
    state => state.authentication?.user?.user?.role
  );
  // Account-team users and executive sponsors list their plans under My Accounts → Account Plans.
  const role =
    userRole || getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER)?.role;
  const hasMyAccounts =
    isAccountTeamRole(role) ||
    normalizeRoleName(role) === ROLES.EXECUTIVE_SPONSOR;
  useEffect(() => {
    void dispatch(fetchAccountPlansEnabled());
  }, [dispatch]);
  // Chat answers from the plan, so it waits until a version has been read.
  const [chatSeed, setChatSeed] = useState<ChatSeed | null>(null);
  const askAboutPlan = useCallback((seed?: AskSeed) => {
    setChatSeed(seed ? { ...seed, nonce: Date.now() } : null);
    setChatOpen(true);
  }, []);

  const update = useCallback(
    (changes: Record<string, string | null>) =>
      setParams(
        prev => {
          const next = new URLSearchParams(prev);
          Object.entries(changes).forEach(([k, v]) =>
            v === null ? next.delete(k) : next.set(k, v)
          );
          return next;
        },
        { replace: true }
      ),
    [setParams]
  );

  // The page follows the user's real role on the account. Someone holding
  // several (vendor admins hold all three) can switch views with ?as=.
  const roles: Persona[] = data?.me.roles.length ? data.me.roles : ["ES"];
  const asParam = params.get("as") as Persona | null;
  const actingPersona: Persona =
    asParam && roles.includes(asParam)
      ? asParam
      : data?.me.roles.length
        ? data.me.default
        : "ES";
  // "Preview as sponsor": any AE/CSM can see exactly what the sponsor sees,
  // read-only, without holding the sponsor role; nothing can be changed there.
  const previewing =
    params.get("preview") === "sponsor" && actingPersona !== "ES";
  const rawTab = params.get("tab") as PlanTab | null;
  // Deep link "Go to the problem" (?focus=problem): open Upload, where the newest
  // upload's problem is shown. Only for the team; the sponsor has no Upload step.
  const focusProblem =
    params.get("focus") === "problem" && actingPersona !== "ES" && !previewing;
  // An AE/CSM opening a published plan with no step in the URL (a shared link)
  // lands on the read-only briefing, as My Accounts opens it — not the review
  // workspace (spec NAV-01, F-41). Only while that published version is still the
  // current one; ?as= (switching role) keeps the role's own landing step.
  const landOnBriefing =
    !!data?.current_review?.published &&
    !rawTab &&
    !asParam &&
    !focusProblem &&
    params.get("view") === null &&
    actingPersona !== "ES" &&
    !previewing;
  // AE/CSM reading the briefing as their own page (how My Accounts opens an
  // account): the sponsor's layout — Briefing / Briefing Questions / View source — read-only,
  // with no "previewing" banner and a way back to the review workspace.
  const reading =
    (params.get("view") === "briefing" || landOnBriefing) &&
    actingPersona !== "ES" &&
    !previewing;
  const persona: Persona = previewing || reading ? "ES" : actingPersona;
  // The sponsor layout. The sponsor and "Preview as sponsor" read the NEWEST usable
  // version, labelled a draft until it is published (Q1 = C + A, Sushant 2026-10-19;
  // replaces F-01's "stay on the last published version"). An AE/CSM reading the
  // briefing as their own page is unchanged: the newest published version, else the
  // current one.
  // During an update the sponsor reads the published version; "View draft" (?draft=1)
  // opens the newer draft, labelled a draft (board 02, 2026-10-31).
  const draftOpen = params.get("draft") === "1";
  const viewData = useMemo(() => {
    if (!data || persona !== "ES") return data;
    const draft = !reading && draftOpen ? data.sponsor_draft : null;
    const id = draft
      ? draft.file_id
      : reading
        ? data.published_file_id
        : data.sponsor_file_id;
    const review = draft
      ? draft.review
      : reading
        ? data.published_review
        : data.sponsor_review;
    // No sponsor version (e.g. the first upload isn't confirmed yet): show nothing
    // rather than falling back to the team's current, unconfirmed version.
    return id
      ? { ...data, current_file_id: id, current_review: review ?? null }
      : { ...data, current_file_id: null, current_review: null };
  }, [data, persona, reading, draftOpen]);
  // Ask about this plan answers only from a confirmed version (decision D6, Phase 2):
  // for the team it shows disabled until one is. The sponsor is unchanged. Only an
  // explicit `confirmed: false` counts, so payloads from before the step still ask.
  // The chat answers from the newest CONFIRMED version, never a newer upload
  // still waiting on Confirm plan (F-UF-F01).
  // The sponsor's layout already points at the version they read
  // (sponsor_file_id, the only one the chat answers a sponsor from).
  // Since 2026-11-01 everyone, the sponsor included, asks about the newest PUBLISHED
  // version only: a sponsor reading a draft from its link still asks about the
  // published one, and nobody asks before the first publish.
  const chatData = useMemo(
    () => (viewData ? withChatFile(viewData) : viewData),
    [viewData]
  );
  const chatGated = !!data && !data.files.some(isChatReady);
  // Someone in the organization who isn't on the account (board 01, 2026-10-31):
  // the server sends only the published briefing; no chat, slides or Reset here.
  const reader = !!data?.me.reader;
  // The sponsor's version has no briefing yet (being written): nothing to ask about,
  // and the server answers no_plan (D47; #1248's gate covers only the team).
  const sponsorNoBriefing =
    !!data &&
    persona === "ES" &&
    !data.files.some(
      f =>
        f.id === chatData?.current_file_id && f.sponsor_view_status === "ready"
    );
  const canAsk =
    chatEnabled &&
    !!chatData?.current_file_id &&
    !chatGated &&
    !reader &&
    !sponsorNoBriefing;

  const tab: PlanTab | null = data
    ? focusProblem
      ? "plan"
      : readerTab(
          reader,
          normalizeTab(
            persona,
            rawTab && TABS.includes(rawTab) ? rawTab : defaultTab(persona, data)
          )
        )
    : null;

  // Keep the URL honest when the tab was defaulted or normalized.
  useEffect(() => {
    if (tab && landOnBriefing) update({ tab, view: "briefing" });
    else if (tab && rawTab !== tab) update({ tab });
  }, [tab, rawTab, landOnBriefing, update]);

  const nav: PlanNav = useMemo(
    () => ({
      // Moving to a step ends a "Go to the problem" deep link.
      go: (t, extra) => update({ tab: t, focus: null, ...(extra || {}) }),
      openSlide: (n, f) =>
        update({
          tab: "viewer",
          focus: null,
          file: f ?? data?.current_file_id ?? null,
          slide: String(n),
        }),
      persona,
      reader,
      setPersona: p => {
        if (roles.includes(p)) update({ as: p, tab: null });
      },
      canAct: role =>
        !previewing &&
        !reading &&
        !!data?.me.roles.includes(role) &&
        persona === role,
      previewing,
      previewAsSponsor: () => update({ preview: "sponsor", tab: "sponsor" }),
      endPreview: () => update({ preview: null, tab: "sponsor" }),
      askAboutPlan: canAsk ? askAboutPlan : undefined,
      backToAccounts: () =>
        navigate(withOrg("/my-portfolio-accounts?tab=account-plans")),
    }),
    // roles is derived from data; data covers it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      update,
      persona,
      data,
      previewing,
      reading,
      navigate,
      withOrg,
      askAboutPlan,
      canAsk,
    ]
  );
  // Confirmed, but its briefing isn't ready yet: say so instead of "confirm".
  const chatGateReason =
    persona === "ES" ||
    (data &&
      data.files.some(
        f =>
          !f.discarded &&
          f.confirmed !== false &&
          f.sponsor_view_status === "ready"
      ))
      ? FLOW.chatNeedsPublish
      : data && data.files.some(awaitsBriefing)
        ? FLOW.chatNeedsBriefing
        : nav.canAct("AE")
          ? FLOW.chatNeedsConfirm
          : FLOW.chatNotConfirmed;

  // The account header and step bar stay pinned while the page scrolls, so the
  // account (sponsor, ARR, renewal) and the step you're on are always in view.
  // Once scrolled, the header drops its breadcrumb and casts a shadow.
  const topRef = useRef<HTMLDivElement>(null);
  const [condensed, setCondensed] = useState(false);
  const hasData = !!data;
  useEffect(() => {
    const sentinel = topRef.current;
    if (!sentinel || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([entry]) => setCondensed(!entry.isIntersecting),
      { root: scrollParent(sentinel) }
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [hasData]);

  // A new step starts at its top, not wherever the last one was scrolled to.
  const lastTab = useRef(tab);
  useEffect(() => {
    if (lastTab.current && tab && lastTab.current !== tab) {
      const pane = scrollParent(topRef.current);
      if (pane) pane.scrollTop = 0;
      else window.scrollTo?.(0, 0);
    }
    lastTab.current = tab;
  }, [tab]);

  // Users with My Accounts start from its Account Plans tab, where the plan came
  // from. Vendor admins, C-level and ESLs reach this page from Account Synopsis
  // and have no My Accounts page, so theirs starts at the account.
  const breadcrumb: PageHeaderBreadcrumb[] = [
    ...(hasMyAccounts
      ? [
          { title: "My Accounts", to: withOrg("/my-portfolio-accounts") },
          {
            title: "Account Plans",
            to: withOrg("/my-portfolio-accounts?tab=account-plans"),
          },
        ]
      : []),
    ...(data
      ? [
          {
            title: data.account.name,
            to: withOrg(`/account-synopsis/${accountId}`),
          },
        ]
      : []),
    { title: "Account Plan" },
  ];

  if (loading && !data) {
    return (
      <div
        className="flex items-center justify-center gap-2 py-24 text-sm text-gray-500"
        role="status"
      >
        <Loader2 className="h-5 w-5 animate-spin text-[#974ae4]" aria-hidden />{" "}
        Loading account plan…
      </div>
    );
  }
  if (!data || !tab) {
    // Not on the account and nothing published (ACC-02, board 01): trying again
    // can't help, so the way out is back to the list.
    if (error === PLAN_NOT_AVAILABLE)
      return (
        <div
          role="alert"
          className="flex flex-col items-start gap-3 p-6 text-sm"
        >
          <p className="text-gray-700">{READER_LOCKED}</p>
          <button
            type="button"
            onClick={() =>
              navigate(withOrg("/my-portfolio-accounts?tab=account-plans"))
            }
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Back to Account Plans
          </button>
        </div>
      );
    // Nothing to show yet: the full-page message, with a way out.
    return (
      <div role="alert" className="flex flex-col items-start gap-3 p-6 text-sm">
        <p className="text-red-600">
          {error || "This account plan couldn't be loaded."}
        </p>
        <button
          type="button"
          onClick={() => void refresh()}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <RefreshCw className="h-4 w-4" aria-hidden /> Try again
        </button>
      </div>
    );
  }

  const primary = data.sponsors
    .filter(s => s.type === "primary")
    .map(s => s.name);
  const sponsors = primary.length ? primary : data.sponsors.map(s => s.name);
  const money = formatMoney(data.account.revenue);
  // One derivation feeds the status strip and the stepper: both describe the
  // version in progress, never a mix of it and an older published one.
  const flow = planFlow(data);
  const reviewDone =
    flow.currentPublished ||
    (!!flow.counts && flow.counts.verified === flow.counts.owned);

  // Step bar: ✓ on done steps (AE/CSM only), plus the Review briefing hint.
  const done = (t: PlanTab): boolean => {
    if (persona === "ES") return false;
    // Done once a version can be read, not as soon as one is uploaded: while
    // the first plan is still being read there's nothing to view yet.
    if (t === "plan") return !!data.current_file_id;
    // Confirm plan ticks once the AE has accepted the current version (Phase 2).
    if (t === "viewer") return !!data.current_file_id && flow.currentConfirmed;
    // Review ticks when every owned section (AE and CSM) is verified (Sushant,
    // 2026-10-19) — or when this version is published: published means review
    // is done, even with a CSM section published unconfirmed (STP-10).
    if (t === "sponsor") return reviewDone;
    // Publish ticks only when the version in progress is the published one. An
    // older published version is reported in the status strip, not here.
    if (t === "publish") return flow.currentPublished;
    return false;
  };
  const reviewHint = flow.currentPublished
    ? "published"
    : flow.counts
      ? `${flow.counts.verified} of ${flow.counts.owned} verified`
      : null;
  const openPublished = flow.published
    ? () =>
        update({
          view: "briefing",
          tab: "sponsor",
          preview: null,
          file: null,
          slide: null,
          focus: null,
        })
    : undefined;
  const activeStep = persona !== "ES" && tab === "qa" ? "sponsor" : tab;
  // The sponsor's layout (sponsor, preview, AE/CSM reading view) with no plan
  // behind it — never uploaded, or reset: Briefing / Briefing Questions / View source would
  // all be empty, so the page shows "No account plan yet" in their place.
  const noPlan = persona === "ES" && !data.files.length;
  const switcher = roles.length > 1 && !previewing && !reading;
  const uploadPlan =
    noPlan && !previewing && data.me.roles.includes("AE")
      ? () => update({ as: "AE", view: null, preview: null, tab: "plan" })
      : undefined;

  return (
    <div className="mt-3 flex min-h-0 flex-col bg-white">
      <div ref={topRef} aria-hidden className="h-px" />
      <div
        data-testid="plan-sticky-header"
        className={`sticky top-0 z-20 bg-white transition-shadow ${
          condensed ? "shadow-[0_4px_12px_-6px_rgba(15,23,42,0.18)]" : ""
        }`}
      >
        <PageHeader
          roomy
          title={data.account.name}
          hideBreadcrumb={condensed}
          icon={ClipboardList}
          breadcrumb={breadcrumb}
          subTitle={
            <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
              <span className="inline-flex items-center gap-1.5">
                <UserCheck className="h-3.5 w-3.5" aria-hidden />
                Sponsor:{" "}
                <span className="font-medium text-gray-700">
                  {sponsors.length ? sponsors.join(", ") : "Not assigned"}
                </span>
              </span>
              {data.team.slice(0, 3).map(m => (
                <span
                  key={`${m.name}-${m.role}`}
                  className="inline-flex items-center gap-1.5"
                >
                  {["CSM", "TAM", "BCS"].includes(m.role.toUpperCase()) ? (
                    <LifeBuoy className="h-3.5 w-3.5" aria-hidden />
                  ) : (
                    <Briefcase className="h-3.5 w-3.5" aria-hidden />
                  )}
                  {m.role}:{" "}
                  <span className="font-medium text-gray-700">{m.name}</span>
                </span>
              ))}
              {money && <span>ARR {money}</span>}
              {data.account.renewal_date && (
                <span>
                  Renews{" "}
                  {parseCalendarDate(
                    data.account.renewal_date
                  ).toLocaleDateString(undefined, {
                    month: "short",
                    year: "numeric",
                  })}
                </span>
              )}
            </span>
          }
        />
        {(error || pollingStopped) && (
          // The page keeps what it has; a failed refresh or a long wait gets a note, not a blank page.
          <div
            role="status"
            className="mx-4 mt-2 flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
          >
            <span>
              {error
                ? `Couldn't refresh this plan: ${error}`
                : "This plan is still processing. Refresh to check on it."}
            </span>
            <button
              type="button"
              onClick={() => void refresh()}
              className="inline-flex shrink-0 items-center gap-1 font-medium text-amber-900 underline-offset-2 hover:underline"
            >
              <RefreshCw className="h-3.5 w-3.5" aria-hidden /> Refresh
            </button>
          </div>
        )}

        {(!noPlan || switcher || reading) && (
          <div className="flex w-full flex-col gap-3 border-b border-gray-200 px-3 py-2.5 md:px-4 lg:flex-row lg:items-center lg:justify-between">
            {noPlan ? (
              <span />
            ) : (
              <nav aria-label="Account plan steps">
                <ol className="flex flex-wrap items-center gap-1">
                  {STEPS[persona]
                    .filter(s => !reader || s.tab !== "viewer")
                    .map((s, i) => {
                      const active = activeStep === s.tab;
                      // Mock v4: the active step keeps its number, never a ✓.
                      const isDone = !active && done(s.tab);
                      return (
                        <li key={s.tab} className="flex items-center">
                          {i > 0 && (
                            <ChevronRight
                              className="mx-1 h-3.5 w-3.5 text-gray-300"
                              aria-hidden
                            />
                          )}
                          <button
                            type="button"
                            onClick={() => nav.go(s.tab)}
                            aria-current={active ? "step" : undefined}
                            className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50 ${
                              active
                                ? "bg-purple-50 font-semibold text-purple-800"
                                : "text-gray-600 hover:bg-gray-50"
                            }`}
                          >
                            <span
                              className={`flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-bold ${
                                isDone
                                  ? "bg-emerald-500 text-white"
                                  : active
                                    ? "bg-[#974ae4] text-white"
                                    : "bg-gray-200 text-gray-600"
                              }`}
                            >
                              {isDone ? (
                                <Check className="h-3 w-3" aria-label="done" />
                              ) : (
                                i + 1
                              )}
                            </span>
                            {s.label}
                            {s.tab === "sponsor" &&
                              persona !== "ES" &&
                              reviewHint && (
                                <span
                                  data-testid="review-hint"
                                  title={
                                    flow.currentPublished
                                      ? PUBLISHED_HINT_HELP
                                      : REVIEW_HINT_HELP
                                  }
                                  aria-description={
                                    flow.currentPublished
                                      ? PUBLISHED_HINT_HELP
                                      : REVIEW_HINT_HELP
                                  }
                                  className={`font-mono text-[11px] ${
                                    flow.currentPublished
                                      ? "text-emerald-700"
                                      : "text-gray-500"
                                  }`}
                                >
                                  {reviewHint}
                                </span>
                              )}
                          </button>
                        </li>
                      );
                    })}
                </ol>
              </nav>
            )}

            <div className="flex flex-wrap items-center gap-3">
              {persona !== "ES" && (
                // Status (STP-11): one chip for the version in progress; its line is the
                // tooltip (upload-screen review, 2 Nov: the line repeated the chip).
                <div
                  data-testid="plan-status-strip"
                  className="flex flex-wrap items-center gap-2"
                >
                  <span className="font-mono text-[10px] font-semibold uppercase tracking-widest text-gray-500">
                    Status
                  </span>
                  <span
                    data-testid="plan-status-chip"
                    title={flow.line}
                    className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${TONE_CLASS[flow.chip.tone]}`}
                  >
                    {flow.chip.label}
                  </span>
                  {/* The chip's line, for screen readers (sighted users hover the chip). */}
                  <span
                    data-testid="plan-status-line"
                    title={
                      flow.olderPublished ? STILL_PUBLISHED_HELP : undefined
                    }
                    className="sr-only"
                  >
                    {flow.line}
                  </span>
                  {flow.published && openPublished && (
                    <button
                      type="button"
                      data-testid="open-published-plan"
                      onClick={openPublished}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1 text-[13px] font-semibold text-slate-600 transition hover:border-purple-300 hover:bg-purple-50 hover:text-[#8538d4] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
                    >
                      <Eye className="h-4 w-4" aria-hidden />{" "}
                      {FLOW.openPublished} (v{flow.published.planVersion})
                    </button>
                  )}
                </div>
              )}
              {reading && (
                <button
                  type="button"
                  onClick={() => update({ view: null, tab: "sponsor" })}
                  title="Plan, review and publish this account's briefing"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[13px] font-semibold text-slate-600 transition hover:border-purple-300 hover:bg-purple-50 hover:text-[#8538d4] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
                >
                  <ClipboardCheck className="h-4 w-4" aria-hidden /> Review
                  workspace
                </button>
              )}
              {switcher && (
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  Viewing as
                  <div
                    role="radiogroup"
                    aria-label="Viewing as"
                    className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5"
                  >
                    {PERSONAS.filter(p => roles.includes(p)).map(p => (
                      <button
                        key={p}
                        type="button"
                        role="radio"
                        aria-checked={persona === p}
                        title={PERSONA_LABEL[p]}
                        onClick={() => nav.setPersona(p)}
                        className={`rounded-md px-2.5 py-1 font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50 ${
                          persona === p
                            ? "bg-white text-gray-900 shadow-sm"
                            : "text-gray-500 hover:text-gray-800"
                        }`}
                      >
                        {PERSONA_SHORT[p]}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {chatEnabled &&
                chatGated &&
                !reader &&
                !noPlan &&
                !!data.current_file_id && (
                  <button
                    type="button"
                    // aria-disabled, not disabled: the tooltip must still show on hover.
                    aria-disabled="true"
                    onClick={e => e.preventDefault()}
                    data-testid="ask-gated"
                    // F-UF-F17: only the AE confirms; anyone else waits on the AE.
                    title={chatGateReason}
                    aria-description={chatGateReason}
                    className="inline-flex cursor-not-allowed items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-[13px] font-semibold text-gray-400"
                  >
                    <Sparkles className="h-4 w-4" aria-hidden /> Ask about this
                    plan
                  </button>
                )}
              {topNext && !noPlan && !previewing && (
                <button
                  type="button"
                  data-testid="next-step-top"
                  onClick={topNext.onClick}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-[#974ae4] px-3 py-1.5 text-[13px] font-semibold text-white transition hover:bg-[#8538d4] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
                >
                  {topNext.label}
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </button>
              )}
              {canAsk && !noPlan && (
                <button
                  type="button"
                  onClick={() => askAboutPlan()}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-purple-200 bg-white px-3 py-1.5 text-[13px] font-semibold text-[#8538d4] transition hover:border-[#974ae4] hover:bg-purple-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
                >
                  <Sparkles className="h-4 w-4" aria-hidden /> Ask about this
                  plan
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="w-full flex-1 bg-white p-3 md:p-4">
        {reader && (
          <div
            role="status"
            data-testid="reader-note"
            className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700"
          >
            <Eye className="h-4 w-4" aria-hidden />
            <span>{READER_NOTE}</span>
          </div>
        )}
        {previewing && (
          <div
            role="status"
            className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-purple-200 bg-purple-50 px-4 py-2.5 text-sm text-purple-900"
          >
            <Eye className="h-4 w-4" aria-hidden />
            <span>
              <span className="font-semibold">Previewing as the sponsor.</span>{" "}
              This is exactly what{" "}
              {sponsors.length ? sponsors.join(", ") : "the sponsor"} sees.
              Nothing can be changed here.
            </span>
            <button
              type="button"
              onClick={nav.endPreview}
              className="ml-auto rounded-md bg-[#974ae4] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#8538d4] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
            >
              Open review workspace
            </button>
          </div>
        )}
        <NextStepSlot.Provider value={setTopNext}>
          {noPlan && <NoPlanYet aeName={aeName(data)} onUpload={uploadPlan} />}
          {!noPlan && tab === "plan" && (
            <PlanOverviewTab
              accountId={accountId}
              data={data}
              nav={nav}
              onChanged={() => void refresh()}
              onOpenViewer={id =>
                update({ tab: "viewer", file: id, slide: null })
              }
              focusProblem={focusProblem}
              startReplace={params.get("replace") === "1"}
              reopenProgress={params.get("progress")}
              onProgressReopened={() => update({ progress: null })}
              onOpenPublished={openPublished || undefined}
              onOpenDraft={() => update({ tab: "sponsor", focus: null })}
            />
          )}
          {!noPlan && tab === "viewer" && (
            <PlanViewerTab
              accountId={accountId}
              data={viewData ?? data}
              nav={nav}
              fileId={fileId}
              slide={slide}
              onSelectFile={id => update({ file: id, slide: null })}
              onSelectSlide={n => update({ slide: String(n) })}
              onGoToPlan={() => nav.go("plan")}
              onChanged={() => void refresh()}
            />
          )}
          {!noPlan && tab === "sponsor" && (
            <SponsorViewTab
              accountId={accountId}
              data={viewData ?? data}
              nav={nav}
              onChanged={() => void refresh()}
              onCite={n => nav.openSlide(n)}
              onGoToPlan={() => nav.go("plan")}
              autoGenerate={params.get("generate") === "1"}
              onAutoGenerated={() => update({ generate: null })}
            />
          )}
          {!noPlan && tab === "qa" && (
            <QaTab accountId={accountId} data={viewData ?? data} nav={nav} />
          )}
          {!noPlan && tab === "publish" && (
            <PublishTab
              accountId={accountId}
              data={data}
              nav={nav}
              onChanged={() => void refresh()}
            />
          )}
        </NextStepSlot.Provider>
      </div>
      {canAsk && (
        <ChatPanel
          open={chatOpen}
          onOpenChange={setChatOpen}
          accountId={accountId}
          data={chatData ?? data}
          nav={nav}
          pageContext={{ tab, slide: tab === "viewer" ? slide : null }}
          seed={chatSeed}
        />
      )}
    </div>
  );
};

export default AccountPlanContainer;
